import { Box, Button, ButtonGroup, Fab, Paper, TableContainer } from "@mui/material"
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Localized, useLocalization } from "@fluent/react"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { loadReleases, Release } from "./ReleasesApi"
import { useNavigate } from "react-router-dom"
import { ReleaseDetailsButton } from "./ReleaseDetailsDialog"
import AddIcon from '@mui/icons-material/Add'
import { If } from "../../../app/If"
import { AddReleaseDialog } from "./AddReleaseDialog"
import { APIError } from "../../../app/appTypes"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"

export const Releases = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [releases, setReleases] = useState<Release[]>([]);
    const [addReleaseDialog, setAddReleaseDialog] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<APIError | null>(null);
    const [isError, setIsError] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadReleases())
            .then(unwrapResult)
            .then(setReleases)
            .catch((error) => { setIsError(true); setError(error); })
            .finally(() => setIsLoading(false));
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleGoToClick = (id: number) => {
        navigate(`/sys/releases/edit/${id}`)
    };

    const columns: GridColDef<Release, any, any>[] = [
        {
            field: 'releaseNo',
            headerName: l10n.getString("lp", null, "_Lp."),
            width: 200
        },
        {
            field: 'title',
            headerName: l10n.getString("releases-title", null, "_Tytuł"),
            width: 200
        },
        {
            field: 'titleEN',
            headerName: l10n.getString("releases-title-en", null, "_Tytuł"),
            width: 200
        },
        {
            field: 'releaseType',
            headerName: l10n.getString("type", null, "_Typ"),
            width: 100
        },
        {
            field: 'releaseVersion',
            headerName: l10n.getString("releases-version", null, "_Wersja"),
            width: 200
        },
        {
            field: 'createDate',
            headerName: l10n.getString("create-date", null, "_Data utworzenia"),
            flex: 1,
            valueGetter: (_, row) => row.createDate ? new Date(row.createDate).toLocaleString() : null
        },
        {
            field: 'status',
            headerName: l10n.getString("status", null, "_Status"),
            flex: 1,
        },
        {
            field: 'publisher',
            headerName: l10n.getString("releases-publisher", null, "_Publikujący"),
            flex: 1,
        },
        {
            field: 'publishDate',
            headerName: l10n.getString("releases-publish-date", null, "_Data publikacji"),
            flex: 1,
            valueGetter: (_, row) => row.publishDate ? new Date(row.publishDate).toLocaleString() : null
        },
        {
            field: 'actions', type: 'actions', width: 350,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <ReleaseDetailsButton loading={false} id={params.row.id.toString()} outlined />
                        <Button
                            onClick={() => handleGoToClick(params.row.id)}
                            color="secondary"
                            variant="outlined" >
                            <Localized id="edit">
                                <span>Edytuj</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    if(isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 175px)' }}
                    rows={releases}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Fab sx={{
                        margin: 1,
                    }}
                    onClick={() => setAddReleaseDialog(true)} color="secondary" size="small" >
                        <AddIcon />
                    </Fab>
                </Box>
            </TableContainer>
            <If condition={addReleaseDialog}>
                <AddReleaseDialog onCancel={() => setAddReleaseDialog(false)}/>
            </If>
        </>
    )
}
